import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import TopBar from './components/TopBar';
import Navbar from './components/Navbar';
import PageWindow from './components/PageWindow';
import Clients from './pages/Clients/Clients';
import ManageClient from './pages/Clients/ManageClient';
import ImportClients from './pages/Clients/ImportClients';
import Users from './pages/Users/Users';
import PrivateRoute from './components/PrivateRoute';
import AgentFiles from './pages/Files/Files';
import SensitiveTopics from './pages/Controls/SensitiveTopics';
import NoGoTopics from './pages/Controls/NoGoTopics';
import Appearance from './pages/Appearance/Appearance';
import Personality from './pages/AiPersonality/AiPersonality';
import BusinessContext from './pages/BusinessContext/BusinessContext';
import AccessControl from './pages/AccessControl/AccessControl';
import useEnsureAppsLoaded from './hooks/useEnsureAppsLoaded';
import UserProbing from './pages/SmartProbing/SmartProbing';
import UserProfileDataModeling from './pages/Personalization/UserProfileDataModeling';
import ProgramProfileDataModeling from './pages/Personalization/ProgramProfileDataModeling';
import AIFinetuning from './pages/Controls/AiFinetuning';
import TextFieldsLoadingPlaceholder from './components/LoadingPlaceholders/TextFieldsLoadingPlaceholder';
import ManageAgents from './pages/Agents/Agents';
import ManageAgent from './pages/Agents/ManageAgent';
import UrlWeightage from './pages/Controls/UrlWeightage';
import DebugLogs from './pages/DebugLogs/DebugLogs';
import ComingSoon from './components/ComingSoon';
import AgentTemplatesList from './pages/AgentStore/AgentTemplatesList';
import AgentTemplateForm from './pages/AgentStore/AgentTemplateForm';
import AgentCapabilitiesTab from './pages/AgentStore/AgentCapabilitiesTab';
import AgentInstancesList from './pages/AgentStore/AgentInstancesList';
import AgentInstanceDetail from './pages/AgentStore/AgentInstanceDetail';
import CapabilitiesStore from './pages/CapabilitiesStore/CapabilitiesStore';
import ManageCapability from './pages/CapabilitiesStore/ManageCapability';
import BusinessUnits from './pages/BusinessUnits/BusinessUnits';
import ManageBusinessUnit from './pages/BusinessUnits/ManageBusinessUnit';
import BusinessUnitDetail from './pages/BusinessUnits/BusinessUnitDetail';
import PromptVariableTemplatesList from './pages/PromptVariableTemplates/PromptVariableTemplatesList';
import PromptVariableTemplateForm from './pages/PromptVariableTemplates/PromptVariableTemplateForm';
import IntentModelsList from './pages/IntentModels/IntentModelsList';
import AgentCapabilities from './pages/Capabilities/Capabilities';
import ManageAgentCapability from './pages/Capabilities/ManageCapability';
import AgentDeploymentsPage from './pages/AgentStore/AgentDeploymentsPage';
import AgentWebsites from './pages/Websites/Websites';
import AgentInsightsPage from './pages/AgentStore/AgentInsightsPage';
import AgentInstructions from './pages/Instructions/Instructions';
import AgentPrompt from './pages/SystemPrompt/SystemPrompt';
import AgentVariables from './pages/PromptVariables/PromptVariables';
import AgentAppView from './pages/EmbeddedView/EmbeddedView';
import AgentChat from './pages/Chat/Chat';
import AgentBotTesting from './pages/BotTesting/AgentBotTesting';
import NotFound from './pages/NotFound/NotFound';
import AgentTypeSelector from './pages/Agents/AgentTypeSelector';
import useApiCall from './hooks/useApiCall';
import { setSelectedAgentMode } from './slices/AppsSlice';
import LoadingSpinner from './components/LoadingSpinner';

// Redirect component for agent base route to profile
function AgentProfileRedirect() {
  const { app_name: appName, agent_id: agentId } = useParams();
  return <Navigate to={`/${appName}/agent/${agentId}/profile`} replace />;
}

// Fetches agent mode when unknown (direct URL nav) and redirects legacy agents to profile.
function LegacyAgentGuard({ element: Element }) {
  const { app_name: appName, id, agent_id } = useParams();
  const agentId = id || agent_id;
  const selectedAgentMode = useSelector(state => state?.apps?.selectedAgentMode);
  const currentApp = useSelector(state => state?.apps?.selectedApp);
  const apps = useSelector(state => state?.apps?.list || []);
  const tokens = useSelector(state => state?.tokens?.value);
  const dispatch = useDispatch();
  const { makeRequest } = useApiCall();
  const [resolvedMode, setResolvedMode] = useState(selectedAgentMode);

  const app = currentApp || apps.find(a => a.url_code === appName);

  useEffect(() => {
    if (selectedAgentMode !== null) {
      setResolvedMode(selectedAgentMode);
      return;
    }
    if (!app?.id || !agentId || !tokens) return;

    makeRequest(`/v1/apps/${app.id}/agents/${agentId}`, { method: 'GET', authTokens: tokens, ignore_error: true })
      .then(data => {
        const agentObj = data?.agent || (data?.id ? data : null);
        const mode = agentObj?.agent_mode || null;
        dispatch(setSelectedAgentMode(mode));
        setResolvedMode(mode);
      })
      .catch(() => setResolvedMode(null));
  }, [selectedAgentMode, app?.id, agentId, tokens]); // eslint-disable-line react-hooks/exhaustive-deps

  if (resolvedMode === null) return <LoadingSpinner />;

  if (resolvedMode === 'legacy') {
    return <Navigate to={`/${appName}/agent/${agentId}/profile`} replace />;
  }
  return <Element />;
}

export default function NavigationRoutes() {
  const [loading, setLoading] = useState(true);
  const currentApp = useSelector(state => state?.apps?.selectedApp);
  const apps = useSelector(state => state?.apps.list || []);
  const location = useLocation();
  const isFirstLoad = useRef(true);

  const ignoredPaths = ['/fonts/']; // Add all static asset paths here

  const isIgnoredPath = ignoredPaths.some(path => location.pathname.startsWith(path));

  // Fetches the apps list and resolves `currentApp` from the URL — runs here,
  // before any route-tree branching below, so it covers every branch
  // (including full-screen routes that render without Navbar).
  useEnsureAppsLoaded();

  useEffect(() => {
    if (apps.length > 0 && isFirstLoad.current) {
      setLoading(false);
      isFirstLoad.current = false;
    }
  }, [apps.length]);

  const applicationRoutes = () => {
    if (!currentApp) {
      return (
        <Route
          path="*"
          element={loading ? <TextFieldsLoadingPlaceholder numSections={1} /> : <NotFound />}
        />
      );
    }

    return (
      <>
        {/* <Route
          path="/:app_name/"
          element={<PrivateRoute element={ManageClient} edit={true} />}
        /> */}
        <Route path="/:app_name/" element={<PrivateRoute element={ManageAgents} edit={true} />} />
        <Route
          path="/:app_name/agents/select-type"
          element={<PrivateRoute element={AgentTypeSelector} />}
        />
        <Route path="/:app_name/agents/new" element={<PrivateRoute element={ManageAgent} />} />
        <Route path="/:app_name/agent/:agent_id" element={<AgentProfileRedirect />} />
        {/* Business Units */}
        <Route
          path="/:app_name/business-units"
          element={<PrivateRoute element={BusinessUnits} />}
        />
        <Route
          path="/:app_name/business-units/:bu_id/agents"
          element={<PrivateRoute element={ManageAgents} />}
        />
        <Route
          path="/:app_name/business-units/new"
          element={<PrivateRoute element={ManageBusinessUnit} />}
        />
        <Route
          path="/:app_name/business-units/:id"
          element={<PrivateRoute element={BusinessUnitDetail} />}
        />
        <Route
          path="/:app_name/business-units/:id/edit"
          element={<PrivateRoute element={ManageBusinessUnit} />}
        />
        {/* Controls AI Personality */}
        <Route
          path="/:app_name/controls/ai-personality"
          element={<PrivateRoute element={Personality} />}
        />

        {/* Controls Business Context */}
        <Route
          path="/:app_name/controls/business-context"
          element={<PrivateRoute element={BusinessContext} />}
        />

        {/* Controls Sensitive Topics */}
        <Route
          path="/:app_name/controls/sensitive-topics"
          element={<PrivateRoute element={SensitiveTopics} />}
        />

        {/* Controls No-Go Topics */}
        <Route
          path="/:app_name/controls/no-go-topics"
          element={<PrivateRoute element={NoGoTopics} />}
        />

        {/* Controls Quality Check */}
        <Route
          path="/:app_name/controls/quality-check"
          element={<PrivateRoute element={AIFinetuning} />}
        />

        <Route
          path="/:app_name/access-control"
          element={<PrivateRoute element={AccessControl} />}
        />
        <Route path="/:app_name/debug-logs" element={<PrivateRoute element={DebugLogs} />} />

        <Route path="/:app_name/admin-downloads" element={<ComingSoon feature="Reports" />} />

        <Route
          path="/:app_name/manage"
          element={<PrivateRoute element={ManageClient} edit={true} />}
        />

        {/* NEW Agent Instance Detail Routes (/:app_name/agent/:id/*) */}
        {/* Profile */}
        <Route
          path="/:app_name/agent/:id/profile"
          element={<PrivateRoute element={ManageAgent} />}
        />

        {/* Prompt */}
        <Route
          path="/:app_name/agent/:id/prompt"
          element={<LegacyAgentGuard element={AgentPrompt} />}
        />

        {/* Instructions */}
        <Route
          path="/:app_name/agent/:id/instructions"
          element={<LegacyAgentGuard element={AgentInstructions} />}
        />

        {/* Capabilities */}
        <Route
          path="/:app_name/agent/:id/capabilities"
          element={<PrivateRoute element={AgentCapabilities} />}
        />
        <Route
          path="/:app_name/agent/:id/capabilities/:cap_id"
          element={<PrivateRoute element={ManageAgentCapability} />}
        />

        {/* Agent Knowledge Base */}
        <Route
          path="/:app_name/agent/:id/knowledge-bases"
          element={<Navigate to="websites" replace />}
        />
        <Route
          path="/:app_name/agent/:id/knowledge-bases/websites"
          element={<PrivateRoute element={AgentWebsites} />}
        />
        <Route
          path="/:app_name/agent/:id/knowledge-bases/files"
          element={<PrivateRoute element={AgentFiles} agentContext={true} />}
        />

        {/* Controls - AI Personality */}
        <Route
          path="/:app_name/agent/:id/controls/ai-personality"
          element={<PrivateRoute element={Personality} />}
        />

        {/* Controls - Sensitive Topics */}
        <Route
          path="/:app_name/agent/:id/controls/sensitive-topics"
          element={<PrivateRoute element={SensitiveTopics} />}
        />

        {/* Controls - No-Go Topics */}
        <Route
          path="/:app_name/agent/:id/controls/no-go-topics"
          element={<PrivateRoute element={NoGoTopics} />}
        />

        {/* Controls - Business Context */}
        <Route
          path="/:app_name/agent/:id/controls/business-context"
          element={<PrivateRoute element={BusinessContext} />}
        />

        {/* Controls - URL Weightage */}
        <Route
          path="/:app_name/agent/:id/controls/url-weightage"
          element={<PrivateRoute element={UrlWeightage} />}
        />

        {/* Knowledge Base - Smart Probing */}
        <Route
          path="/:app_name/agent/:id/knowledge-bases/smart-probing"
          element={<PrivateRoute element={UserProbing} />}
        />

        {/* Knowledge Base - Program Data Model */}
        <Route
          path="/:app_name/agent/:id/knowledge-bases/program-data-model"
          element={<PrivateRoute element={ProgramProfileDataModeling} />}
        />

        {/* Personalization - User Profile */}
        <Route
          path="/:app_name/agent/:id/personalization/user-profile-data-model"
          element={<PrivateRoute element={UserProfileDataModeling} />}
        />
        <Route
          path="/:app_name/agent/:id/controls/manage-user-profile"
          element={<PrivateRoute element={UserProfileDataModeling} />}
        />
        <Route
          path="/:app_name/agent/:id/user-profiling/user-profile-data-model"
          element={<PrivateRoute element={UserProfileDataModeling} />}
        />

        {/* Personalization - Program Profile */}
        <Route
          path="/:app_name/agent/:id/personalization/program-profile-data-model"
          element={<PrivateRoute element={ProgramProfileDataModeling} />}
        />
        <Route
          path="/:app_name/agent/:id/controls/manage-program-profile"
          element={<PrivateRoute element={ProgramProfileDataModeling} />}
        />

        {/* Channels */}
        <Route path="/:app_name/agent/:id/channels" element={<ComingSoon feature="Channels" />} />

        {/* Deployments */}
        <Route
          path="/:app_name/agent/:id/deployments"
          element={<PrivateRoute element={AgentDeploymentsPage} />}
        />

        {/* Insights - InSync User Profile */}
        <Route
          path="/:app_name/agent/:id/insights/user-profile"
          element={
            <PrivateRoute element={() => <AgentInsightsPage insightType="user-profile" />} />
          }
        />

        {/* Insights - Business Insights */}
        <Route
          path="/:app_name/agent/:id/insights/business-insights"
          element={
            <PrivateRoute element={() => <AgentInsightsPage insightType="business-insights" />} />
          }
        />

        {/* Settings - Chat Appearance */}
        <Route
          path="/:app_name/agent/:id/settings/appearance"
          element={<PrivateRoute element={Appearance} />}
        />

        {/* Settings - AI Testing */}
        <Route
          path="/:app_name/agent/:id/settings/bot-testing"
          element={<LegacyAgentGuard element={AgentBotTesting} />}
        />

        {/* Agent Chat — full-screen, no layout chrome */}
        <Route path="/:app_name/agent/:agent_id/chat" element={<LegacyAgentGuard element={AgentChat} />} />

        {!isIgnoredPath && <Route path="*" element={<NotFound />} />}
      </>
    );
  };

  // Chat route gets a full-screen layout (no TopBar / Navbar / PageWindow)
  const isChatRoute = /\/agent\/[^/]+\/chat$/.test(location.pathname);

  // Agent embedded app — render without any chrome
  if (/^\/[^/]+\/agent\/[^/]+\/view$/.test(location.pathname)) {
    return (
      <Routes>
        <Route
          path="/:app_name/agent/:agent_id/view"
          element={<PrivateRoute element={AgentAppView} />}
        />
      </Routes>
    );
  }

  if (isChatRoute) {
    return (
      <Routes>
        <Route path="/:app_name/agent/:agent_id/chat" element={<LegacyAgentGuard element={AgentChat} />} />
      </Routes>
    );
  }

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      <Navbar />
      <div
        style={{
          marginLeft: 'var(--sidebar-width, 208px)',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          overflow: 'hidden',
          transition: 'margin-left 0.2s ease',
        }}
      >
        <TopBar />
        <div style={{ flex: 1, overflowY: 'auto', overflowX: 'auto' }}>
          <PageWindow>
            <Routes>
              <Route path="/" element={<Clients />} />
              <Route path="/clients" element={<Clients />} />
              <Route path="/clients/new" element={<ManageClient />} />
              <Route path="/clients/import" element={<ImportClients />} />
              <Route path="/admin/users" element={<PrivateRoute element={Users} />} />
              <Route path="/users" element={<Navigate to="/admin/users" replace />} />
              <Route
                path="/admin/agent-variables"
                element={<PrivateRoute element={AgentVariables} />}
              />
              <Route path="/admin/billing" element={<ComingSoon feature="Billing" />} />
              <Route path="/admin/system-status" element={<ComingSoon feature="System Status" />} />
              {/* V2 Agent Templates Routes */}
              <Route
                path="/admin/agent-templates"
                element={<PrivateRoute element={AgentTemplatesList} />}
              />
              <Route
                path="/admin/agent-templates/new"
                element={<PrivateRoute element={AgentTemplateForm} />}
              />
              <Route
                path="/admin/agent-templates/:id"
                element={<PrivateRoute element={AgentTemplateForm} />}
              />
              <Route
                path="/admin/agent-templates/:id/edit"
                element={<PrivateRoute element={AgentTemplateForm} />}
              />
              <Route
                path="/admin/agent-templates/:id/capabilities"
                element={<PrivateRoute element={AgentCapabilitiesTab} />}
              />

              {/* V2 Capabilities Routes */}
              <Route
                path="/admin/capabilities"
                element={<PrivateRoute element={CapabilitiesStore} />}
              />
              <Route
                path="/admin/capabilities/new"
                element={<PrivateRoute element={ManageCapability} />}
              />
              <Route
                path="/admin/capabilities/:id/edit"
                element={<PrivateRoute element={ManageCapability} />}
              />

              {/* V2 Prompt Variable Templates Routes */}
              <Route
                path="/admin/prompt-variable-templates"
                element={<PrivateRoute element={PromptVariableTemplatesList} />}
              />
              <Route
                path="/admin/prompt-variable-templates/new"
                element={<PrivateRoute element={PromptVariableTemplateForm} />}
              />
              <Route
                path="/admin/prompt-variable-templates/:id/edit"
                element={<PrivateRoute element={PromptVariableTemplateForm} />}
              />

              {/* Agent Instances Routes */}
              <Route
                path="/agent-instances"
                element={<PrivateRoute element={AgentInstancesList} />}
              />
              <Route
                path="/agent-instances/:id"
                element={<PrivateRoute element={AgentInstanceDetail} />}
              />

              <Route
                path="/admin/ai-models/intent-models"
                element={<PrivateRoute element={IntentModelsList} />}
              />
              {applicationRoutes()}
            </Routes>
          </PageWindow>
        </div>
      </div>
    </div>
  );
}
